/**
 * WelcomeIntro (#127): three screens on the app's first launch that explain the
 * whole loop before the Home: you write questions only one person knows, you
 * send a link they open in the browser, and at the end there is your message
 * and maybe their video. Skippable; App stores the flag (storage.introSeen).
 *
 * The pictures are real app screens, one set per language, in public/intro/.
 * The palette is the house one (DESIGN.md candy_heart), not the theme vars: the
 * intro comes before any theme is picked.
 */
import React, { useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";

const LINGUE = ["it", "en", "es", "fr"];
const PASSI = [
  { key: "create", web: false, sticker: "💭" },
  { key: "link", web: true, sticker: "🔗" },
  { key: "message", web: true, sticker: "💌" },
];

const C = {
  bg: "#fcf5f7",
  ink: "#4a1030",
  muted: "#7a5a68",
  primary: "#a8275a",
  bright: "#ff709f",
  dot: "#ecc9d6",
};

function linguaImmagini(i18n) {
  const lng = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  return LINGUE.includes(lng) ? lng : "en";
}

export default function WelcomeIntro({ onDone }) {
  const { t, i18n } = useTranslation();
  const [passo, setPasso] = useState(0);
  const toccoX = useRef(null);
  const ultimo = passo === PASSI.length - 1;
  const { key, web, sticker } = PASSI[passo];
  const lingua = linguaImmagini(i18n);
  const base = process.env.PUBLIC_URL || "";

  const avanti = () => (ultimo ? onDone() : setPasso(passo + 1));
  const indietro = () => setPasso((p) => Math.max(0, p - 1));
  const onTouchStart = (e) => { toccoX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (toccoX.current == null) return;
    const dx = e.changedTouches[0].clientX - toccoX.current;
    toccoX.current = null;
    if (dx < -50 && !ultimo) setPasso(passo + 1);
    if (dx > 50) indietro();
  };

  // La cornice si adatta all'altezza: su un telefono basso resta spazio per testo e pulsante.
  const larghezzaCornice = "min(70vw, 290px, calc((100dvh - 400px) * 0.672))";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("app.intro.aria")}
      data-testid="welcome-intro"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        display: "flex", flexDirection: "column",
        background: `radial-gradient(120% 60% at 100% 0%, #ffd6e4 0%, rgba(255,214,228,0) 60%), radial-gradient(90% 50% at 0% 100%, #ffe6dc 0%, rgba(255,230,220,0) 60%), ${C.bg}`,
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes puzzlyIntroIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .puzzly-intro-anim { animation: puzzlyIntroIn .32s cubic-bezier(.2,.8,.2,1); }
        .puzzly-intro-cta:active { transform: scale(.96); }
        @media (prefers-reduced-motion: reduce) { .puzzly-intro-anim { animation: none; } }
      `}</style>

      <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 12px 0", minHeight: 48 }}>
        <button
          type="button"
          onClick={onDone}
          data-testid="welcome-intro-skip"
          style={{
            visibility: ultimo ? "hidden" : "visible",
            background: "transparent", border: "none", cursor: "pointer",
            padding: "10px 14px", borderRadius: 999,
            fontFamily: "var(--font-body)", fontSize: 15, fontWeight: 600, color: C.muted,
          }}
        >
          {t("app.intro.skip")}
        </button>
      </div>

      <div key={passo} className="puzzly-intro-anim" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px" }}>
        <div style={{ position: "relative", width: larghezzaCornice, flexShrink: 0 }}>
          <div style={{
            background: "#ffffff", padding: 6, borderRadius: 30,
            boxShadow: "0 30px 60px -28px rgba(122,14,60,0.45), 0 10px 30px -14px rgba(168,39,90,0.20)",
          }}>
            <img
              src={`${base}/intro/${lingua}-${passo + 1}.webp`}
              alt=""
              draggable="false"
              style={{ display: "block", width: "100%", aspectRatio: "430 / 640", objectFit: "cover", borderRadius: 24, background: "#f6eff1" }}
            />
          </div>
          {web && (
            <div aria-hidden="true" style={{
              position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
              padding: "6px 14px", borderRadius: 999, background: "#ffffff",
              boxShadow: "0 8px 20px -10px rgba(122,14,60,0.35)",
              fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600, color: C.muted,
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
              puzzly.giadalabs.com
            </div>
          )}
          <span aria-hidden="true" style={{
            position: "absolute", right: -14, bottom: 26, fontSize: 34,
            transform: "rotate(12deg)", filter: "drop-shadow(0 6px 10px rgba(122,14,60,0.25))",
          }}>{sticker}</span>
        </div>

        <div style={{ marginTop: 28, minHeight: 140, textAlign: "center", maxWidth: 360 }} aria-live="polite">
          <h1 style={{
            margin: 0, fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28,
            lineHeight: 1.1, letterSpacing: "-0.02em", color: C.ink, textWrap: "balance",
          }}>
            <Trans i18nKey={`app.intro.steps.${key}.title`} components={{ c: <span style={{ color: C.primary }} /> }} />
          </h1>
          <p style={{
            margin: "10px 0 0", fontFamily: "var(--font-body)", fontSize: 16, lineHeight: 1.5, color: C.muted,
          }}>
            {t(`app.intro.steps.${key}.body`)}
          </p>
        </div>
      </div>

      <div style={{ padding: "18px 24px 22px" }}>
        <div aria-hidden="true" style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 18 }}>
          {PASSI.map((p, i) => (
            <span key={p.key} style={{
              width: i === passo ? 22 : 8, height: 8, borderRadius: 999,
              background: i === passo ? C.primary : C.dot, transition: "width .25s ease, background .25s ease",
            }} />
          ))}
        </div>
        <button
          type="button"
          className="puzzly-intro-cta"
          onClick={avanti}
          data-testid="welcome-intro-next"
          style={{
            display: "block", width: "100%", maxWidth: 420, margin: "0 auto", height: 56,
            border: "none", borderRadius: 999, cursor: "pointer",
            background: `linear-gradient(135deg, ${C.primary}, ${C.bright})`, color: "#ffffff",
            fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 800,
            boxShadow: "0 12px 28px -12px rgba(168,39,90,0.55)", transition: "transform .12s ease",
          }}
        >
          {ultimo ? t("app.intro.start") : t("app.intro.next")}
        </button>
      </div>
    </div>
  );
}
